import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  Router
} from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import * as fromUserProfile from '@shared/user-profile.reducer';
import { ApplicationStatusEnums } from '@modules/shared/enums/common.enum';
import { ROUTE_PATHS } from '@constants/routes';
import {
  TABS_TO_SHOW_WHEN_PENDING_CREATOR
} from '@modules/shared/constants/tab-approval.constant';
import { CommonService } from '@modules/shared/services/common.service';
import { TABS_TO_SHOW_WHEN_APPROVED_CREATOR } from '@modules/shared/constants/tab-approval.constant';
import { AuthService } from '@core/auth.service';

@Injectable({
  providedIn: 'root'
})
export class CreatorApprovalGuard implements CanActivate {
  private deferredCheckActive = false;
  private readonly isBrowser: boolean;

  constructor(
    private readonly router: Router,
    private readonly cs: CommonService,
    private readonly store: Store<fromUserProfile.UserProfileState>,
    private readonly authService: AuthService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  canActivate(
    route: ActivatedRouteSnapshot
  ): boolean {
    const tabKeyFromRoute = route.data['tabKey'] as string | undefined;


    if (tabKeyFromRoute == null) {
      return true;
    }

    if(!!this.authService.userContext?.fm_user) return true;

    const userProfile = this.cs.getLoggedInUser();

    if (!userProfile || Array.isArray(userProfile)) {
      this.deferredApprovalCheck(tabKeyFromRoute);
      return true;
    }

    return this.evaluateApproval(userProfile, tabKeyFromRoute);
  }

  private evaluateApproval(userProfile: any, tabKey: string): boolean {
    const status = userProfile?.admin_approval_status;
    const isPending = status === ApplicationStatusEnums.PENDING;
    const isResubmitted = status === ApplicationStatusEnums.RESUBMIT;
    const isCreatorApproved =
      status === ApplicationStatusEnums.APPROVED || !!userProfile?.is_creator;
    const isWhiteLabelWebsite = this.authService.isWhiteLabelWebsite();
    let shouldShowTab = true;
    if (isPending || isResubmitted) {
      shouldShowTab = !TABS_TO_SHOW_WHEN_PENDING_CREATOR.includes(tabKey);
    } else if (isCreatorApproved && !isWhiteLabelWebsite) {
      shouldShowTab = !TABS_TO_SHOW_WHEN_APPROVED_CREATOR.includes(tabKey);
    }

    if (!shouldShowTab) {
      this.router.navigateByUrl(this.getBlockedTabRedirectPath(userProfile));
      return false;
    }

    return true;
  }

  private deferredApprovalCheck(tabKey: string): void {
    if (!this.isBrowser || this.deferredCheckActive) {
      return;
    }
    this.deferredCheckActive = true;

    this.store.select(fromUserProfile.getUserProfileItems).pipe(
      filter((p: any) => !!p && !Array.isArray(p)),
      take(1)
    ).subscribe((userProfile: any) => {
      this.deferredCheckActive = false;

      const status = userProfile?.admin_approval_status;
      const isPending = status === ApplicationStatusEnums.PENDING;
      const isResubmitted = status === ApplicationStatusEnums.RESUBMIT;
      const isCreatorApproved =
        status === ApplicationStatusEnums.APPROVED || !!userProfile?.is_creator;
      const isWhiteLabelWebsite = this.authService.isWhiteLabelWebsite();

      let shouldShowTab = true;
      if (isPending || isResubmitted) {
        shouldShowTab = !TABS_TO_SHOW_WHEN_PENDING_CREATOR.includes(tabKey);
      } else if (isCreatorApproved && !isWhiteLabelWebsite) {
        shouldShowTab = !TABS_TO_SHOW_WHEN_APPROVED_CREATOR.includes(tabKey);
      }
      if (!shouldShowTab) {
        this.router.navigateByUrl(this.getBlockedTabRedirectPath(userProfile));
      }
    });
  }

  getBlockedTabRedirectPath(userProfile: any): string {
    const status = userProfile?.admin_approval_status;
    const isPendingOrResubmitted =
      status === ApplicationStatusEnums.PENDING ||
      status === ApplicationStatusEnums.RESUBMIT;
    if (isPendingOrResubmitted) {
      return ROUTE_PATHS.EDIT_PREFERENCE_PATH;
    }

    if (this.authService.isWhiteLabelWebsite()) {
      return this.cs.getWhiteLabelHomePath();
    }

    if (userProfile?.is_creator) {
      return ROUTE_PATHS.EDIT_PREFERENCE_WHITELABEL;
    }

    const isCreatorApproved =
      status === ApplicationStatusEnums.APPROVED || !!userProfile?.is_creator;
    if (isCreatorApproved && userProfile?.user_name) {
      return `/${userProfile.user_name}`;
    }

    return ROUTE_PATHS.DASHBOARD_PATH;
  }
}
